import { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  getLocalStorage,
  removeLocalStorage,
  setLocalStorage,
} from "../helpers/common";
import { UserSocialSignOn } from "../services/v2/socialSignOns";
import { getAuthUrl } from "../helpers/v2/socialSignOns";

export const useOAuthLogin = ({ onAuthSuccess }) => {
  const [authCode, setAuthCode] = useState(null);
  const [platform, setPlatform] = useState(getLocalStorage("provider") || null);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");
    const error = urlParams.get("error");

    if (code && !window.opener) {
      localStorage.setItem("auth_code", code);
      setAuthCode(code);
      self.close();
    }

    if (error) {
      console.error("OAuth Error:", error);
      window.close();
    }
  }, []);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "auth_code" && event.newValue) {
        setAuthCode(event.newValue);
        removeLocalStorage("auth_code");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  useEffect(() => {
    const handleAuthCode = async () => {
      if (authCode && platform && !isProcessing) {
        setIsProcessing(true);
        try {
          const response = await UserSocialSignOn({
            code: authCode,
            provider: platform,
            isFromMobile: false,
          });

          setAuthCode(null);
          setPlatform(null);
          removeLocalStorage("provider");
          onAuthSuccess?.(response, platform);
        } catch (error) {
          console.error("OAuth error:", error);
          toast.error(
            `An error occurred during ${platform} login. Please try again.`,
          );

          setAuthCode(null);
          setPlatform(null);
          removeLocalStorage("provider");
        } finally {
          setIsProcessing(false);
        }
      }
    };

    handleAuthCode();
  }, [authCode, platform, isProcessing]);

  const openLoginPopup = async (
    url,
    popupName,
    popupWidth = 600,
    popupHeight = 600,
  ) => {
    const popupTop =
      window.top.outerHeight / 2 + window.top.screenY - popupHeight / 2;
    const popupLeft =
      window.top.outerWidth / 2 + window.top.screenX - popupWidth / 2;

    const loginPopup = window.open(
      url,
      popupName,
      `width=${popupWidth},height=${popupHeight},top=${popupTop},left=${popupLeft}`,
    );

    if (!loginPopup) {
      throw new Error("Popup blocked. Please allow popups for this site.");
    }

    return loginPopup;
  };

  const handleOAuthLogin = async (provider) => {
    try {
      const authUrl = getAuthUrl(provider);
      setPlatform(provider === "apple-mobile" ? "apple" : provider);
      setLocalStorage("provider", provider);

      const popup = await openLoginPopup(
        authUrl.toString(),
        `${provider} Login`,
      );

      const checkPopupResult = setInterval(() => {
        try {
          const popupUrl = popup.location.href;
          if (popupUrl.includes("?code=") || popupUrl.includes("?error=")) {
            const urlParams = new URLSearchParams(popup.location.search);
            const code = urlParams.get("code");

            if (code) {
              localStorage.setItem("auth_code", code);
              setAuthCode(code);
              popup.close();
              clearInterval(checkPopupResult);
            }
          }
        } catch (error) {
        }

        if (popup.closed) {
          if (!localStorage.getItem("auth_code")) {
            if (provider !== "apple")
              toast.error(`${provider} login failed or cancelled`);
          }
          localStorage.removeItem("auth_code");
          localStorage.removeItem("provider");
          clearInterval(checkPopupResult);
        }
      }, 500);
    } catch (error) {
      toast.error(
        error.message || "An error occurred. Please try again later.",
      );
      removeLocalStorage("auth_code");
      removeLocalStorage("provider");
    }
  };

  return { handleOAuthLogin };
};
